import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, DatePicker, Form, Modal, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useTRPC } from '@/trpc/react.ts'
import { FinanceSelect } from './finance-select.tsx'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

export function MonthlyGroupSupplement({ onClose }: { onClose: () => void }) {
  const [open, setOpen] = useState(true)
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp()
  const [customerId, setCustomerId] = useState<number>()
  const [month, setMonth] = useState(() => dayjs().subtract(1, 'month'))
  const [key, setKey] = useState(() => crypto.randomUUID())
  const input = { customerId: customerId ?? 1, month: month.format('YYYY-MM') }
  const preview = useQuery({ ...trpc.bill.previewMonthlyGroup.queryOptions(input), enabled: !!customerId })
  const save = useMutation(trpc.bill.supplementMonthlyGroup.mutationOptions())
  const changeScope = () => setKey(crypto.randomUUID())
  return (
    <Modal
      open={open}
      title="按客户月份补组"
      width="60rem"
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      confirmLoading={save.isPending}
      okText="生成收款草稿"
      okButtonProps={{ disabled: !customerId || !preview.data?.bills.length || preview.isFetching || preview.isError }}
      onOk={async () => {
        try {
          await save.mutateAsync({ ...input, idempotencyKey: key })
          await Promise.all([
            refreshMoneyQueries(cache, trpc),
            cache.invalidateQueries(trpc.bill.automationRuns.queryFilter())
          ])
          void message.success('已按经营主体生成收款草稿，请核对账户后确认出单')
          setOpen(false)
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '补组失败')
        }
      }}
    >
      <div className="text-content-muted mb-4 flex flex-wrap items-center gap-x-2 text-sm">
        <span>仅纳入所选月份的正式未收应收，按经营主体分别生成。</span>
        <Tooltip
          title="已占用和结清账单会排除。可用此入口处理更早月份欠款、迟录费用和原组作废后的账单。"
          trigger={['hover', 'focus', 'click']}
        >
          <Button type="link" size="small">
            补组规则
          </Button>
        </Tooltip>
      </div>
      <Form layout="vertical" className="grid grid-cols-1 gap-x-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Form.Item label="客户" htmlFor="supplement-customer" required>
          <FinanceSelect
            purpose="bill.group.read"
            id="supplement-customer"
            kind="customer"
            value={customerId}
            disabled={save.isPending}
            onChange={(v) => {
              setCustomerId(v)
              changeScope()
            }}
          />
        </Form.Item>
        <Form.Item label="组单归属月份" htmlFor="supplement-month" required>
          <DatePicker
            className="w-full"
            id="supplement-month"
            picker="month"
            allowClear={false}
            value={month}
            disabled={save.isPending}
            onChange={(v) => {
              if (v) {
                setMonth(v)
                changeScope()
              }
            }}
          />
        </Form.Item>
      </Form>
      {preview.isError && <Alert type="error" title={preview.error.message} />}
      {preview.data && (
        <p className="text-content-muted mb-3 text-sm" role="status">
          可补组 <strong className="text-content">{preview.data.bills.length}</strong> 张；待核费 {preview.data.pending}{' '}
          张
        </p>
      )}
      <Table
        rowKey="id"
        size="small"
        pagination={{ pageSize: 10 }}
        loading={!!customerId && preview.isFetching}
        dataSource={preview.data?.bills ?? []}
        columns={[
          { title: 'ID', dataIndex: 'id' },
          { title: '账单号', dataIndex: 'billNo' },
          { title: '主体 ID', dataIndex: 'organizationId' },
          { title: '费用', dataIndex: 'description' },
          { title: '当前待收', dataIndex: 'outstanding' }
        ]}
      />
    </Modal>
  )
}
